import React, { useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Grid, Divider, Chip, Tooltip, Badge,
  Card, CardContent, LinearProgress, Stack, Modal, Tabs, Tab, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton
} from '@mui/material';
import {
  AddCircleOutline, CheckCircle, Schedule, WarningAmber,
  MedicalServices, WaterDrop, ShowChart, ArrowUpward, ArrowDownward,
  Close, Save, EditNotifications, Block, Restaurant, SettingsInputComponent,
  HistoryEdu, LocalHospital
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// --- DATOS INICIALES COMPLETOS ---
const MOCK_PACIENTE = {
  nombre: 'Luna', especie: 'Felino', raza: 'Europeo común', peso_actual: 4.75,
  alertas: ['Alérgica a Penicilina', 'Agresiva al manipular cola'],
  vet_responsable: 'Dra. Sofía Ramírez'
};

const ACCESOS_INVASIVOS = [
  { id: 1, tipo: 'Vía IV Cefálica', fecha: '2026-02-07', lado: 'Derecha', estado: 'Permeable' },
  { id: 2, tipo: 'Sonda Uretral', fecha: '2026-02-08', lado: 'N/A', estado: 'Funcional' }
];

const BALANCE_HIDRICO = [
  { hora: '14:00', ingresos: 15, ingresos_tipo: 'Fluido IV', egresos: 12, egresos_tipo: 'Orina', obs: 'Micción voluntaria' },
  { hora: '12:00', ingresos: 10, ingresos_tipo: 'Fluido IV', egresos: 0, egresos_tipo: '-', obs: '-' },
];

const TRATAMIENTOS = [
  { id: 1, med: 'Ceftriaxona', dosis: '120mg', via: 'IV', horas: ['08:00', '20:00'], completadas: ['08:00'], estado: 'activo' },
  { id: 2, med: 'Metoclopramida', dosis: '2.5mg', via: 'SC', horas: ['06:00', '14:00', '22:00'], completadas: ['06:00', '14:00'], estado: 'activo' },
  { id: 3, med: 'Enalapril', dosis: '0.5mg/kg', via: 'PO', horas: ['10:00'], completadas: [], estado: 'suspendido', motivo_suspension: 'Hipotensión' },
];

const NUTRICION = [
  { hora: '09:00', alimento: 'Recovery Diet', cantidad: '50g', consumo: 40, tipo: 'Voluntaria' }
];

const EVOLUCION = [
  { hora: '14:00', temp: 38.5, fc: 120, fr: 30, mucosas: 'Rosadas', dolor: 2, obs: 'Estable', autor: 'Vet. Sofía' },
];

// Funciones de utilidad
  const calcularHorasUso = (fecha) => dayjs().diff(dayjs(fecha), 'hour');

// Estilos
const modalStyle = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: { xs: '95%', md: 750 }, bgcolor: 'background.paper', borderRadius: 3,
  boxShadow: 24, p: 0, overflow: 'hidden'
};

const HospitalizacionIntegral = () => {
  const [openModal, setOpenModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Estado para Accesos (Haciéndolo dinámico)
  const [accesos, setAccesos] = useState([
    { id: 1, tipo: 'Vía IV Cefálica', fecha: '2026-02-10T08:00', lado: 'Derecha', estado: 'Permeable', calibre: '22G' },
    { id: 2, tipo: 'Sonda Uretral', fecha: '2026-02-12T14:00', lado: 'N/A', estado: 'Funcional', calibre: '8 Fr' }
  ]);

  // Cálculos rápidos
  const totalIn = BALANCE_HIDRICO.reduce((acc, curr) => acc + curr.ingresos, 0);
  const totalOut = BALANCE_HIDRICO.reduce((acc, curr) => acc + curr.egresos, 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: '#f1f5f9', minHeight: '100vh' }}>
        
        {/* 1. HEADER Y ACCESOS RÁPIDOS */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="caption" fontWeight="bold" color="error">CRITICAL CARE UNIT</Typography>
              <Typography variant="h4" fontWeight="900" color="#0f172a">{MOCK_PACIENTE.nombre.toUpperCase()}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {MOCK_PACIENTE.alertas.map((a, i) => (
                  <Chip key={i} label={a} size="small" color="error" icon={<WarningAmber />} />
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={1}>
                {accesos.map((ac) => {
                  const horas = calcularHorasUso(ac.fecha);
                  const alerta = horas > 72; // Alerta si lleva más de 3 días
                  return (
                    <Grid item xs={6} key={ac.id}>
                      <Tooltip title={`Puesto hace ${horas} horas. Calibre: ${ac.calibre}`}>
                        <Box sx={{ 
                          p: 1, bgcolor: alerta ? '#fff1f2' : '#f8fafc', 
                          borderRadius: 2, border: alerta ? '1px solid #fca5a5' : '1px solid #e2e8f0', 
                          display: 'flex', alignItems: 'center', position: 'relative'
                        }}>
                          <SettingsInputComponent sx={{ fontSize: 18, mr: 1, color: alerta ? '#ef4444' : '#6366f1' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                              {ac.tipo} ({horas}h)
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem', color: alerta ? '#b91c1c' : 'inherit' }}>
                              {ac.lado !== 'N/A' ? `${ac.lado} - ` : ''} {ac.estado}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
              <Button variant="contained" fullWidth onClick={() => setOpenModal(true)} startIcon={<AddCircleOutline />} sx={{ borderRadius: 2, bgcolor: '#2563eb', py: 1.5 }}>
                Registrar Actividad Clínica
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {/* 2. MEDICACIÓN (CON ESTADOS) */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
              <Box sx={{ p: 2, bgcolor: '#1e40af', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Schedule sx={{ mr: 1 }} /> PLAN DE MEDICACIÓN
                </Typography>
                <Chip label="75% Completado" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell>FÁRMACO</TableCell>
                      <TableCell align="center">ESTADO</TableCell>
                      <TableCell align="right">HORARIOS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {TRATAMIENTOS.map((t) => (
                      <TableRow key={t.id} sx={{ bgcolor: t.estado === 'suspendido' ? '#fff1f2' : 'inherit' }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', textDecoration: t.estado === 'suspendido' ? 'line-through' : 'none' }}>{t.med}</Typography>
                          <Typography variant="caption" color="text.secondary">{t.dosis} • {t.via}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={t.estado} size="small" color={t.estado === 'activo' ? 'success' : 'error'} sx={{ fontSize: '0.6rem', height: 20 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {t.horas.map(h => (
                              <Chip key={h} label={h} size="small" color={t.completadas.includes(h) ? "success" : "default"} sx={{ fontSize: '0.6rem', height: 18 }} />
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* 3. MONITOREO CLÍNICO */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
              <Box sx={{ p: 2, bgcolor: '#475569', color: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShowChart sx={{ mr: 1 }} /> ÚLTIMA EVOLUCIÓN
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell>HORA</TableCell>
                      <TableCell align="center">VITALES</TableCell>
                      <TableCell align="center">DOLOR</TableCell>
                      <TableCell>OBSERVACIONES</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {EVOLUCION.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{e.hora}</TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">{e.temp}°C | {e.fc} bpm</Typography>
                          <Typography variant="caption">{e.mucosas}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Badge badgeContent={e.dolor} color="primary"><MedicalServices sx={{ fontSize: 18, color: '#94a3b8' }} /></Badge>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{e.obs}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* 4. BALANCE HÍDRICO (EXTENDIDO) */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
              <Box sx={{ p: 2, bgcolor: '#0f172a', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <WaterDrop sx={{ mr: 1, color: '#38bdf8' }} /> BALANCE HÍDRICO (IN/OUT)
                </Typography>
                <Typography variant="caption">Neto: <b>{totalIn - totalOut} ml</b></Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell>HORA</TableCell>
                      <TableCell align="right">INGRESOS</TableCell>
                      <TableCell align="right">EGRESOS</TableCell>
                      <TableCell>OBSERVACIONES</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {BALANCE_HIDRICO.map((b, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{b.hora}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main' }}>+{b.ingresos} ml</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>-{b.egresos} ml</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{b.obs}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* 5. NUTRICIÓN */}
          <Grid item xs={12} lg={5}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
              <Box sx={{ p: 2, bgcolor: '#10b981', color: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Restaurant sx={{ mr: 1 }} /> ALIMENTACIÓN
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell>ALIMENTO</TableCell>
                      <TableCell align="center">CONSUMO</TableCell>
                      <TableCell>TIPO</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {NUTRICION.map((n, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{n.alimento}</Typography>
                          <Typography variant="caption">{n.cantidad}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <LinearProgress variant="determinate" value={n.consumo} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                          <Typography variant="caption">{n.consumo}%</Typography>
                        </TableCell>
                        <TableCell><Chip label={n.tipo} size="small" variant="outlined" sx={{ fontSize: '0.6rem' }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* --- MODAL UNIFICADO --- */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box sx={modalStyle}>
            <Box sx={{ p: 2, bgcolor: '#1e293b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">Registro Hospitalario</Typography>
              <IconButton onClick={() => setOpenModal(false)} sx={{ color: 'white' }}><Close /></IconButton>
            </Box>
            
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab icon={<ShowChart />} label="Vitales" sx={{ fontSize: '0.65rem' }} />
              <Tab icon={<WaterDrop />} label="Fluidos" sx={{ fontSize: '0.65rem' }} />
              
              <Tab icon={<Restaurant />} label="Dieta" sx={{ fontSize: '0.65rem' }} />
              <Tab icon={<EditNotifications />} label="Ajustar Plan" sx={{ fontSize: '0.65rem' }} />
              <Tab icon={<SettingsInputComponent />} label="Accesos" /> {/* NUEVA PESTAÑA */}
            </Tabs>

            <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={4}><TextField fullWidth label="Temp" size="small" /></Grid>
                  <Grid item xs={4}><TextField fullWidth label="FC" size="small" /></Grid>
                  <Grid item xs={4}><TextField fullWidth label="FR" size="small" /></Grid>
                  <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Observaciones de evolución" size="small" /></Grid>
                </Grid>
              )}

              {tabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><TextField fullWidth label="Ingreso (ml)" type="number" size="small" /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Egreso (ml)" type="number" size="small" /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Origen/Destino" size="small" /></Grid>
                </Grid>
              )}

              {tabValue === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={8}><TextField fullWidth label="Tipo de Alimento" size="small" /></Grid>
                  <Grid item xs={4}><TextField fullWidth label="% Consumo" type="number" size="small" /></Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo de Ingesta</InputLabel>
                      <Select label="Tipo de Ingesta">
                        <MenuItem value="vol">Voluntaria</MenuItem>
                        <MenuItem value="for">Forzada / Sonda</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {tabValue === 3 && (
                <Stack spacing={2}>
                  <Typography variant="caption" fontWeight="bold" color="error">SUSPENDER MEDICAMENTOS:</Typography>
                  {TRATAMIENTOS.filter(t => t.estado === 'activo').map(t => (
                    <Box key={t.id} sx={{ p: 1, border: '1px solid #fee2e2', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{t.med}</Typography>
                      <Button size="small" variant="text" color="error" startIcon={<Block />}>Suspender</Button>
                    </Box>
                  ))}
                  <Divider />
                  <Typography variant="caption" fontWeight="bold" color="primary">NUEVA INDICACIÓN:</Typography>
                  <TextField fullWidth label="Medicamento" size="small" />
                  <Grid container spacing={1}>
                    <Grid item xs={6}><TextField fullWidth label="Dosis" size="small" /></Grid>
                    <Grid item xs={6}><TextField fullWidth label="Frecuencia" size="small" /></Grid>
                  </Grid>
                </Stack>
              )}           

              {tabValue === 4 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">NUEVO ACCESO / CATÉTER</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo de Dispositivo</InputLabel>
                      <Select label="Tipo de Dispositivo">
                        <MenuItem value="iv">Vía Intravenosa</MenuItem>
                        <MenuItem value="su">Sonda Uretral</MenuItem>
                        <MenuItem value="sng">Sonda Nasogástrica</MenuItem>
                        <MenuItem value="tt">Tubo de Tórax</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Calibre (G o Fr)" size="small" placeholder="Ej: 22G o 8Fr" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Ubicación" size="small" placeholder="Ej: Cefálica Derecha" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth type="datetime-local" label="Fecha de Instalación" size="small" InputLabelProps={{ shrink: true }} defaultValue={dayjs().format('YYYY-MM-DDTHH:mm')} />
                  </Grid>
                  
                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom color="error" fontWeight="bold">RETIRAR ACCESOS ACTUALES</Typography>
                    {accesos.map(ac => (
                      <Box key={ac.id} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, border: '1px solid #e2e8f0', mb: 1, borderRadius: 2 }}>
                        <Typography variant="body2">{ac.tipo} ({ac.lado})</Typography>
                        <Button size="small" color="error" variant="outlined" startIcon={<Block />}>Retirar</Button>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              )}   
            </Box>

            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => setOpenModal(false)}>Cancelar</Button>
              <Button fullWidth variant="contained" startIcon={<Save />} sx={{ bgcolor: '#2563eb' }}>Guardar Todo</Button>
            </Box>
          </Box>
        </Modal>

      </Box>
    </LocalizationProvider>
  );
};

export default HospitalizacionIntegral;